import { FunctionComponent, useEffect, useState } from "react";
import BasicDrawer from "@components/drawers/BasicDrawer";
import {
  formValidateTriggers,
  YOURTE_SDK_API_KEY,
  YOURTE_SDK_BASE_URL,
  YOURTE_SDK_DEFAULT_VERSION
} from "@utils/Constant";
import { Form } from "antd";
import MailFormField, { EMAIL_REGEX } from "@components/inputs/MailFormField";
import { useForm } from "antd/lib/form/Form";
import BasicButton from "@components/buttons/BasicButton";
import ContactCard from "@components/cards/ContactCard";
import InputFormField from "@components/inputs/InputFormField";
import { SelectFormField } from "@components/inputs/SelectFormField";
import BasicCard from "@components/cards/BasicCard";
import TextAreaFormField from "@components/inputs/TextAreaFormField";
import { ContactDto } from "@type/estimateInput/estimateInput.dto";
import { toastSuccess } from "@utils/toast-helper";

import DepartmentsData from "src/static/data/departements-region.json";
import { useStore } from "effector-react";
import { sessionStore } from "@state/session/SessionStore";
import HTMLReactParser from "html-react-parser";
import { useTranslation } from "react-i18next";
import {useGoogleReCaptcha} from "@utils/react-google-recaptcha-v3/use-google-recaptcha";

interface SaveDrawerProps {
  visible: boolean;
  isCollectivity: boolean;
  saveSimulation: (
    contactDto: ContactDto,
    successCallback: (simulationId: string) => void,
    errorCallback: () => void
  ) => void;
  onUpdateSimulationId: (id: string) => void;
  onClose: () => void;
  className?: string;
}

const SaveDrawer: FunctionComponent<SaveDrawerProps> = ({
  visible,
  isCollectivity,
  saveSimulation,
  onUpdateSimulationId,
  onClose,
  className = "",
}) => {
  const sessionData = useStore(sessionStore);

  const { executeRecaptcha } = useGoogleReCaptcha();

  const [form] = useForm();
  const { t } = useTranslation();

  const InitialValues = {
    email: "",
  };

  const [isFormComplete, setIsFormComplete] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isSharingChecked, setIsSharingChecked] = useState<boolean>(false);
  const [hasSelectedCompany, setHasSelectedCompany] = useState<boolean>(false);
  const [showEmptyCompanyError, setShowEmptyCompanyError] =
    useState<boolean>(false);

  const formatInput = (input: any) => (input != null ? input : "");

  useEffect(() => {
    if (visible && isSharingChecked) {
      // @ts-ignore
      const client = new window.YourteSDK.Client({
        apiKey: YOURTE_SDK_API_KEY,
        server: `${YOURTE_SDK_BASE_URL}${(window as any)?.yourte_sdk_version ? 
          (window as any)?.yourte_sdk_version : YOURTE_SDK_DEFAULT_VERSION}`,
      });
      // @ts-ignore
      if (window.YourteSDK.Autocomplete) {
        // @ts-ignore
        const autocomplete = new window.YourteSDK.Autocomplete(
          client,
          "#denomination",
          "uniteLegale.periodesUniteLegale.0.denominationUniteLegale",
          {
            "uniteLegale.siren": "#siren",
            "etablissement.siret": "#siret",
            yourteId: "#yourteid",
          },
          {
            containerCustomClass: "yourte-header",
            addHighlight: true,
            callbackSelect: function (item: any) {
              const business = item.etablissement;
              const adresse = business.adresseEtablissement;
              if (business) {
                setHasSelectedCompany(true);
                setShowEmptyCompanyError(false);
              }
              form.setFields([
                { name: "siren", value: business.siren },
                { name: "siret", value: business.siret },
                {
                  name: "localisation",
                  value: adresse.codePostalEtablissement.substring(0, 2),
                },
                {
                  name: "denomination",
                  value:
                    item.uniteLegale.periodesUniteLegale[0]
                      .denominationUniteLegale,
                },
                {
                  name: "legalCategory",
                  value:
                    item.uniteLegale.periodesUniteLegale[0]
                      .categorieJuridiqueUniteLegale,
                },
                {
                  name: "salariedEmployeesTranche",
                  value: business.trancheEffectifsEtablissement,
                },
                {
                  name: "municipalityName",
                  value: adresse.libelleCommuneEtablissement,
                },
                {
                  name: "trackDescription",
                  value: adresse.libelleVoieEtablissement,
                },
                { name: "inseeCode", value: adresse.codeCommuneEtablissement },
                {
                  name: "mainActivity",
                  value:
                    item.uniteLegale.periodesUniteLegale[0]
                      .activitePrincipaleUniteLegale,
                },
                {
                  name: "address",
                  value: [
                    formatInput(adresse.numeroVoieEtablissement),
                    formatInput(adresse.typeVoieEtablissement),
                    formatInput(adresse.libelleVoieEtablissement),
                    formatInput(adresse.libelleCommuneEtablissement),
                    formatInput(adresse.codePostalEtablissement),
                  ]
                    .filter((a) => a !== "")
                    .join(" "),
                },
              ]);
              validateForm(
                {
                  denomination:
                    item.uniteLegale.periodesUniteLegale[0]
                      .denominationUniteLegale,
                  localisation: adresse.codePostalEtablissement.substring(0, 2),
                  siret: business.siret,
                },
                form.getFieldsValue()
              );
            },
          }
        );
      }
    }
  }, [visible, isSharingChecked]);

  const checkEmailFilled = () => {
    return (
      form.getFieldValue("email") &&
      form.getFieldValue("email").match(EMAIL_REGEX)
    );
  };

  const validateForm = (
    changedValues: { [key: string]: string },
    values: { [key: string]: string }
  ) => {
    const requiredFields = isCollectivity
      ? ["name", "localisation", "phone"]
      : ["name", "localisation", "phone", "siret"];
    if (
      checkEmailFilled() &&
      (!isSharingChecked ||
        requiredFields.every(
          (field) =>
            (values[field] && values[field].length > 0) ||
            (changedValues.hasOwnProperty(field) &&
              changedValues[field] &&
              changedValues[field].length > 0)
        ))
    ) {
      setIsFormComplete(true);
    } else {
      setIsFormComplete(false);
    }
  };

  const handleCheckContact = (value: boolean) => {
    setIsSharingChecked(value);
    if (!value && checkEmailFilled()) {
      setIsFormComplete(true);
    } else {
      setIsFormComplete(false);
    }
  };

  const handleCompanyInputBlur = () => {
    if (!hasSelectedCompany) {
      setShowEmptyCompanyError(true);
      const companyInput = document.getElementById(
        "denomination"
      ) as HTMLInputElement;
      if (companyInput) companyInput.value = "";
    } else setShowEmptyCompanyError(false);
  };

  const buildContactDto = (token: string): ContactDto | undefined => {
    if (token) {
      const contactDto: ContactDto = form.getFieldsValue();
      contactDto.captcha = token;
      contactDto.origin = sessionData?.origin;
      return contactDto;
    } else {
      return undefined;
    }
  };

  const handleSubmit = (values: any) => {
    handleSaveSimulation();
  };

  const handleSaveSimulation = async () => {
    if (executeRecaptcha) {
      const token = await executeRecaptcha();

      const contactDto = buildContactDto(token);
      if (contactDto) {
        setIsUploading(true);

        const successCallback = (simulationId: string) => {
          onUpdateSimulationId(simulationId);
          if (contactDto.email) {
            if (isSharingChecked) {
              toastSuccess(
                "Félicitations, votre simulation a été sauvegardée ! Vous allez bientôt recevoir un email contenant l’accès à votre simulation. Un expert de votre département vous recontactera dès que possible pour répondre à toutes vos questions."
              );
            } else {
              toastSuccess(
                "Félicitations, votre simulation a été sauvegardée ! Vous allez bientôt recevoir un email contenant l’accès à votre simulation."
              );
            }
            form.resetFields();
            onClose();
          }
          setIsUploading(false);
        };

        const errorCallback = () => {
          setIsUploading(false);
        };

        saveSimulation(contactDto, successCallback, errorCallback);
      }
    }
  };

  return (
    <BasicDrawer
      title="Enregistrer votre simulation"
      visible={visible}
      onClose={onClose}
      className={className}
    >
      <>
        <Form
          {...formValidateTriggers}
          form={form}
          validateTrigger={"onChange"}
          layout="vertical"
          initialValues={InitialValues}
          onFinish={handleSubmit}
          onValuesChange={validateForm}
          className="contact-form"
        >
          <MailFormField
            required
            showLabel
            module="save"
            field="email"
            label={
              <span>
                {HTMLReactParser(
                  "Votre adresse email <span className='font-weight-normal'>(exemple@gmail.com)</span>"
                )}
              </span>
            }
            placeholder="Votre adresse email (exemple@gmail.com)"
            emptyErrorMessage="Votre adresse email pour enregistrer votre simulation"
            className="required-not-displayed"
          />
          <InputFormField
            className={"d-none"}
            module="save"
            field="isSimulationOwner"
            initialValue={"true"}
            label="Nom et prénom"
            placeholder="Nom et prénom"
          />
          {(isFormComplete || isSharingChecked) && (
            <ContactCard className="mb-4" onCheck={handleCheckContact}>
              <>
                <InputFormField
                  required
                  showLabel
                  module="save"
                  field="name"
                  label="Nom et prénom"
                  placeholder="Nom et prénom"
                />
                <div className="ant-row ant-form-item">
                  <div className="ant-col ant-form-item-label">
                    <label
                      htmlFor="denomination"
                      className={`${
                        !isCollectivity ? "ant-form-item-required" : ""
                      }`}
                    >
                      <span
                        data-test="input-label"
                        data-test-for="denomination"
                      >
                        Entreprise
                      </span>
                    </label>
                  </div>
                  <div className="ant-col ant-form-item-control">
                    <div
                      className="ant-form-item-control-input"
                      id="yourte-container"
                    >
                      <input
                        id="denomination"
                        required={!isCollectivity}
                        className="ant-input"
                        type="text"
                        placeholder="Raison sociale de l'entreprise"
                        onBlur={handleCompanyInputBlur}
                      />
                    </div>
                    {showEmptyCompanyError && (
                      <div className="ant-form-item-explain ant-form-item-explain-error">
                        Vous devez sélectionner une entreprise dans la liste
                        déroulante.
                      </div>
                    )}
                  </div>
                </div>
                <InputFormField
                  className={"d-none"}
                  module="save"
                  field="address"
                  label="address"
                />
                <InputFormField
                  className={"d-none"}
                  module="save"
                  field="siret"
                  label="siret"
                />
                <InputFormField
                  className={"d-none"}
                  module="save"
                  field="siren"
                  label="siren"
                />
                <InputFormField
                  className={"d-none"}
                  module="save"
                  field="denomination"
                  label="denomination"
                />
                <InputFormField
                  className={"d-none"}
                  module="save"
                  field="legalCategory"
                  label="legalCategory"
                />
                <InputFormField
                  className={"d-none"}
                  module="save"
                  field="salariedEmployeesTranche"
                  label="salariedEmployeesTranche"
                />
                <InputFormField
                  className={"d-none"}
                  module="save"
                  field="municipalityName"
                  label="municipalityName"
                />
                <InputFormField
                  className={"d-none"}
                  module="save"
                  field="trackDescription"
                  label="trackDescription"
                />
                <InputFormField
                  className={"d-none"}
                  module="save"
                  field="inseeCode"
                  label="inseeCode"
                />
                <InputFormField
                  className={"d-none"}
                  module="save"
                  field="mainActivity"
                  label="mainActivity"
                />
                <SelectFormField
                  required
                  showLabel
                  module="save"
                  field="localisation"
                  label="Votre département"
                  placeholder="Votre département"
                  options={DepartmentsData.map((department) => {
                    return {
                      key: department.num_dep.toString(),
                      value: department.num_dep.toString(),
                      label: `${department.num_dep} - ${department.dep_name}`,
                    };
                  })}
                />
                <InputFormField
                  required
                  showLabel
                  type="text"
                  module="save"
                  field="phone"
                  label="Numéro de téléphone"
                  placeholder="Numéro de téléphone"
                  maxLength={12}
                  rules={[
                    {
                      pattern: new RegExp(/^[+\d]+$/),
                      message: "Les caractères [0-9] et + sont autorisés",
                    },
                  ]}
                />
                <BasicCard className="card-small card-grey mb-3">
                  <SelectFormField
                    showLabel
                    module="save"
                    field="contactPreference"
                    label="Quand préférez-vous être rappelé ?"
                    placeholder="Choisissez un créneau"
                    options={[
                      {
                        value: "matin",
                        label: "Le matin",
                      },
                      {
                        value: "midi",
                        label: "Le midi",
                      },
                      {
                        value: "soir",
                        label: "Le soir",
                      },
                      {
                        value: "aucune",
                        label: "Pas de préférence",
                      },
                    ]}
                    className="mb-0"
                  />
                </BasicCard>
                <TextAreaFormField
                  showLabel
                  module="save"
                  field="note"
                  label="Motif de votre demande"
                  placeholder="Motif de votre demande"
                  className="mb-0"
                />
              </>
            </ContactCard>
          )}
          <BasicButton
            type="submit"
            variant="secondary"
            size="lg"
            text="M’envoyer les résultats par email"
            className="w-100"
            disabled={!isFormComplete || !executeRecaptcha || isUploading}
          />
          <p className="text-sm mt-3">
            {HTMLReactParser(t("captcha.description"))}
          </p>
        </Form>
      </>
    </BasicDrawer>
  );
};

export default SaveDrawer;
